import { extend } from 'vee-validate'
import {
  email,
  min,
  max,
  min_value,
  max_value,
  required,
  numeric,
} from 'vee-validate/dist/rules'

export const validationFunctions = {
  // Valor precisa ser maior que {value}
  min_value() {
    extend('min_value', {
      ...min_value,
      params: ['min'],
      message: 'Valor mínimo de {min}',
    })
  },
  // Valor precisa ser menor que {value}
  max_value() {
    extend('max_value', {
      ...max_value,
      params: ['max'],
      message: 'Valor máximo de {max}',
    })
  },
  // Validação de nome completo (Precisa de pelo menos 2 palavras com 3 caracteres cada)
  name() {
    extend('name', (value) => {
      const regex = /^([A-zÀ-ú '´]{3,})+\s+([A-zÀ-ú '´]{3,})$/g
      if (regex.test(value)) return true
      return 'Digite um nome completo'
    })
  },
  // Caso 1: Checa se um usuário, hoje, tem no mínimo {args} anos
  // Definido por: Um argumento único na validação
  // Exemplo: minAge:18 - Checa se o usuário teria no mínimo 18 anos se ele tiver nascido em {value}.
  // Caso 2: Checa se um usuário, em {args[1]}, tem no mínimo {args[0]} anos
  // Definido por: Dois ou mais argumentos na validação
  // Exemplo: minAge:18,2025-01-01 - Checa se o usuário teria no mínimo 18 anos em 2025-01-01
  minAge() {
    extend('minAge', {
      validate(value, args) {
        if (typeof args === 'string' || args.length === 1) {
          const age = typeof args === 'string' ? args : args[0]
          // Value é uma data do tipo dd/mm/aaaa ou aaaa/mm/dd
          if (value.length < 10) return 'Data inválida'

          let parsedDate = value
          if (value.includes('/')) {
            parsedDate = value.split('/').reverse().join('-')
          }

          const today = format(new Date(), 'yyyy-MM-dd')
          const ageDiff = differenceInYears(
            parseISO(today),
            parseISO(parsedDate)
          )

          if (ageDiff >= age) return true
          return `Idade mínima de ${age} anos`
        } else if (Array.isArray(args)) {
          const age = args[0]
          if (!isDateValid(value)) return 'Data Inválida'
          const dateToCheck = args[1]
          const ageDiff = differenceInYears(
            parseISO(dateToCheck),
            parseISO(value)
          )
          if (ageDiff < parseInt(age)) {
            if (args[2] && args[2].includes('custom_travel_date_msg')) {
              return `Usuário precisa ter ao menos ${age} anos na data de viagem`
            } else return `Idade mínima de ${age} anos`
          }
        }
        return true
      },
    })
  },
  // Caso 1: Checa se um usuário, hoje, tem no máximo {args} anos
  // Definido por: Um argumento único na validação
  // Exemplo: maxAge:65 - Checa se o usuário teria no máximo 65 anos se ele tiver nascido em {value}.
  // Caso 2: Checa se um usuário, em {args[1]}, tem no máximo {args[0]} anos
  // Definido por: Dois ou mais argumentos na validação
  // Exemplo: maxAge:65,2025-01-01 - Checa se o usuário teria no máximo 65 anos em 2025-01-01
  maxAge() {
    extend('maxAge', {
      validate(value, args) {
        if (typeof args === 'string' || args.length === 1) {
          const age = typeof args === 'string' ? args : args[0]
          if (!isDateValid(value)) {
            return 'Data Inválida'
          }

          let parsedDate = value
          if (value.includes('/')) {
            parsedDate = value.split('/').reverse().join('-')
          }

          const today = format(new Date(), 'yyyy-MM-dd')
          const ageDiff = differenceInYears(
            parseISO(today),
            parseISO(parsedDate)
          )

          if (ageDiff <= age) return true
          return `Idade máxima de ${age} anos`
        } else if (Array.isArray(args)) {
          const age = args[0]
          if (!isDateValid(value)) return 'Data Inválida'
          const dateToCheck = args[1]
          const ageDiff = differenceInYears(
            parseISO(dateToCheck),
            parseISO(value)
          )
          if (ageDiff > parseInt(age)) {
            if (args[2] && args[2].includes('custom_return_date_msg')) {
              return `Usuário precisa ter no máximo ${age} anos na data de retorno`
            } else return `Idade máxima de ${age} anos`
          }
          return true
        }
      },
    })
  },
  isBeforeToday() {
    extend('isBeforeToday', {
      validate(value) {
        const defaultResponse = 'Data Inválida'

        if (!isDateValid(value)) {
          return defaultResponse
        }

        const today = new Date()
          .toLocaleDateString()
          .split('/')
          .reverse()
          .join('-')
        if (isAfter(new Date(value), new Date(today))) {
          return 'Data precisa ser igual ou antes de hoje'
        }
        return true
      },
    })
  },
  // Verifica se a data inputada vem depois de uma data informada.
  // Caso 1: Passar 2 argumentos (< ou <=) para saber se a data comparada deve ser incluída nos valores possíveis
  // Caso 2: Passar apenas o argumento 'today' para checar se a data é pelo menos depois do que hoje
  minDate() {
    extend('minDate', {
      validate(value, arg) {
        const errorText = 'Data inválida'
        if (!isDateValid(value)) return errorText
        if (arg.length > 0) {
          const condition = arg.map((item) => item.split(/;/g))

          for (const conditional of condition) {
            if (conditional[0] === 'today') {
              if (isAfter(new Date(value), subDays(new Date(), 1))) return true
              else return 'Data precisa ser a partir de hoje'
            } else if (conditional[0] === '<') {
              return isBefore(new Date(value), new Date(conditional[1]))
                ? true
                : errorText
            } else if (conditional[0] === '<=') {
              return isAfter(new Date(value), new Date(conditional[1]))
                ? errorText
                : true
            }
          }
        }
        return true
      },
    })
  },
  date() {
    extend('date', {
      validate(value, arg) {
        const errorText = 'Data inválida'
        if (!isDateValid(value)) return errorText
        if (arg.length > 0) {
          const condition = arg.map((item) => item.split(/;/g))

          for (const conditional of condition) {
            if (conditional[0] === '<') {
              return isBefore(new Date(value), new Date(conditional[1]))
                ? true
                : errorText
            } else if (conditional[0] === '<=') {
              return isAfter(new Date(value), new Date(conditional[1]))
                ? errorText
                : true
            }
          }
        }
        return true
      },
    })
  },
  // Validação de CPF
  cpf() {
    extend('cpf', {
      validate(value) {
        const cleanCpf = value.replace(/\D/g, '')
        let Soma
        let Resto
        Soma = 0

        const valueWithoutRepeatedCharacters = cleanCpf
          .split('')
          .filter((character, index, self) => {
            return self.indexOf(character) === index
          })
          .join('')

        if (valueWithoutRepeatedCharacters.length <= 1) return 'CPF inválido'

        for (let i = 1; i <= 9; i++) {
          Soma = Soma + parseInt(cleanCpf.substring(i - 1, i)) * (11 - i)
        }
        Resto = (Soma * 10) % 11

        if (Resto === 10 || Resto === 11) Resto = 0
        if (Resto !== parseInt(cleanCpf.substring(9, 10))) return 'CPF inválido'

        Soma = 0
        for (let i = 1; i <= 10; i++) {
          Soma = Soma + parseInt(cleanCpf.substring(i - 1, i)) * (12 - i)
        }
        Resto = (Soma * 10) % 11

        if (Resto === 10 || Resto === 11) Resto = 0
        if (Resto !== parseInt(cleanCpf.substring(10, 11))) {
          return 'CPF inválido'
        }
        return true
      },
    })
  },
  // Validação de e-mail
  email() {
    extend('email', {
      ...email,
      message: 'E-mail inválido',
    })
  },
  // Validação de campo obrigatório
  required() {
    extend('required', {
      ...required,
      message: 'Campo obrigatório',
    })
  },
  // Validação de mínimo de caracteres
  min() {
    extend('min', {
      ...min,
      params: ['length'],
      message: 'Mínimo de {length} caracteres',
    })
  },
  // Validação de máximo de caracteres
  max() {
    extend('max', {
      ...max,
      params: ['length'],
      message: 'Máximo de {length} caracteres',
    })
  },
  // Validação se o valor é exatamente igual ao outro (Arrumar msg caso precise de situração diferente de confirmação de senha)
  equal() {
    extend('equal', {
      params: ['target'],
      validate(value, { target }) {
        return value === target
      },
      message: 'Senhas precisam ser iguais',
    })
  },
  // Checa se o valor é maior do que um especificado
  minValue() {
    extend('minValue', {
      validate(value, minimum) {
        const val = typeof value === 'number' ? value : parseFloat(value)
        if (val >= minimum) return true
        return `Valor mínimo de ${minimum}`
      },
    })
  },
  /**
   * Checa se um valor é maior que outro especificado
   * @param {String} maximum - Valor máximo a ser checado
   */
  maxValue() {
    extend('maxValue', {
      validate(value, maximum) {
        const val = typeof value === 'number' ? value : parseFloat(value)
        if (val <= maximum) return true
        return `Valor máximo de ${maximum}`
      },
    })
  },
  // Validação de senha
  password() {
    extend('password', {
      validate(value) {
        const hasUpperCase = (str) => {
          return str.toLowerCase() !== str
        }

        const message =
          'Senha deve conter 8+ caracteres, com no mínimo 1 letra maiúscula e 1 caractere especial.'
        const symbolRegex = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/
        if (value.length < 8) return message
        if (!symbolRegex.test(value)) return message
        if (!hasUpperCase(value)) return message
        return true
      },
    })
  },
  // Validação de senha
  passwordEsusFeedback() {
    extend('passwordEsusFeedback', {
      validate(value) {
        const message = 'Senha deve conter no mínino 5 caracteres'

        if (value.length < 5) return message

        return true
      },
    })
  },
  // Validação de CEP via busca por API
  cep() {
    extend('cep', {
      async validate(value) {
        if (value.replace(/\D/g, '').length === 8) {
          const cep = value.replace(/\D/g, '')
          let valid = false

          // Multi services for fallback
          try {
            const responseViacep = await axios.get(
              `https://viacep.com.br/ws/${cep}/json/`
            )
            if (responseViacep.data.erro) valid = false
            else valid = true
          } catch (errViacep) {
            try {
              await axios.get(`https://api.postmon.com.br/v1/cep/${cep}`)
              valid = true
            } catch (errPostmon) {
              try {
                const responseRepublica = await axios.get(
                  `http://cep.republicavirtual.com.br/web_cep.php?cep=${cep}&formato=json`
                )
                if (
                  !responseRepublica.data.resultado_txt.includes(
                    'cep não encontrado'
                  )
                ) {
                  valid = true
                } else valid = false
              } catch (errRepublica) {
                valid = false
              }
            }
          }
          return valid ? true : 'CEP inválido'
        }
        return 'CEP inválido'
      },
    })
  },
  // Validação bem básica de número de telefone (Checa apenas se o não são vários caracteres repetidos)
  phone() {
    extend('phone', {
      validate(value) {
        const regex = /^[0-9]{8}|^[0-9]{9}$/
        const cleanValue = value.replace(/\D/g, '')

        const valueWithoutRepeatedCharacters = cleanValue
          .split('')
          .filter((character, index, self) => {
            return self.indexOf(character) === index
          })
          .join('')

        if (valueWithoutRepeatedCharacters.length <= 1) {
          return 'Telefone inválido'
        }

        return regex.test(cleanValue) ? true : 'Telefone inválido'
      },
    })
  },
  isDate() {
    extend('isDate', {
      validate(value) {
        if (!isDateValid(value)) return 'Data inválida'
        return true
      },
    })
  },
  // Valida se possui ao menos um caractere especial
  specialChar() {
    extend('specialChar', {
      validate(value) {
        return /[^A-Za-z0-9]+/.test(value) || 'Mínimo de um caractere especial'
      },
    })
  },
  // Valida se possui ao menos uma letra maiúscula
  uppercaseChar() {
    extend('uppercaseChar', {
      validate(value) {
        return /(?=.*[A-Z])/.test(value) || 'Mínimo de uma letra maiúscula'
      },
    })
  },
  // Valida se possui ao menos um número
  numericChar() {
    extend('numericChar', {
      validate(value) {
        return /[0-9]+/.test(value) || 'Mínimo de um número'
      },
    })
  },
  onlyNumbers() {
    extend('onlyNumbers', (value) => {
      const regex = /^\d+$/
      if (regex.test(value)) return true
      return 'Este campo deve conter apenas números.'
    })
  },
}

export const validations = () => {
  for (const item in validationFunctions) {
    validationFunctions[item]()
  }
}
